import './App.css';
import React, { useState, useEffect } from "react";
import Menu from './Menu';
import Filter from './Filter.js';
import StatusModal from './StatusModal';
import DisclaimerModal from './DisclaimerModal';
import Gridbutton from './Gridbutton';
import CreatureDetail from './CreatureDetail';
import { bugData } from './bugData';
import { fishData } from './fishData';
import { seaData } from './seaData';
import { getCreatureInfo } from './creatureDatabase';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import bg from './img/background/background.jpg';
import blathersImg from './img/blathers.png'; 

function App() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedHemisphere, setSelectedHemisphere] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  
  const navigate = useNavigate();
  
  // Handle 404 redirect from sessionStorage
  useEffect(() => {
    const redirect = sessionStorage.redirect;
    if (redirect && redirect !== '/') {
      delete sessionStorage.redirect;
      navigate(redirect, { replace: true });
    }
  }, [navigate]);
  
  // Filter modal state
  const [savedFilters, setSavedFilters] = useState({
    hemisphere: 'north', // Default to north hemisphere
    months: new Set(),
    timeRange: { start: 0, end: 23 },
    isTimeFilterActive: false,
    caughtFilter: 'both' // Default to show both caught and uncaught
  });
  const [collectedItems, setCollectedItems] = useState(() => {
    try {
      // First try to load from profile collections
      const profileCollections = localStorage.getItem('profileCollections');
      const activeProfile = localStorage.getItem('activeProfile') || 'save1';
      
      if (profileCollections) {
        const parsedProfiles = JSON.parse(profileCollections);
        return parsedProfiles[activeProfile] || [];
      }
      
      // Fallback to old collectedItems for backward compatibility
      const saved = localStorage.getItem('collectedItems');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading initial data:', error);
      return [];
    }
  });
  const [activeProfile, setActiveProfile] = useState(() => {
    const saved = localStorage.getItem('activeProfile');
    return saved || 'save1';
  });

  // Save active profile to localStorage and load its collection when it changes
  useEffect(() => {
    localStorage.setItem('activeProfile', activeProfile);
    
    // Load the collection for the new active profile
    try {
      const profileCollections = JSON.parse(localStorage.getItem('profileCollections') || '{}');
      if (profileCollections[activeProfile]) {
        setCollectedItems(profileCollections[activeProfile]);
      } else {
        // If no collection exists for this profile, start with empty
        setCollectedItems([]);
      }
    } catch (error) {
      console.error('Error loading profile collection:', error);
      setCollectedItems([]);
    }
  }, [activeProfile]);

  // Save to localStorage when collected items change
  useEffect(() => {
    localStorage.setItem('collectedItems', JSON.stringify(collectedItems));   
  }, [collectedItems]);

  // Function to toggle item in lower gridbox
  const toggleItemCollection = (itemName) => {
    setCollectedItems(prev => {
      const newCollection = prev.includes(itemName) 
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName];
      
      // Update the profile collections in localStorage
      try {
        const profileCollections = JSON.parse(localStorage.getItem('profileCollections') || '{}');
        profileCollections[activeProfile] = newCollection;
        localStorage.setItem('profileCollections', JSON.stringify(profileCollections));
      } catch (error) {
        console.error('Error updating profile collections:', error);
      }
      
      return newCollection;
    });
  };

  // Function to check if creature is available in selected filters
  const isCreatureAvailable = (creature) => {
    // Check caught/uncaught filter first
    const isCollected = collectedItems.includes(creature.name);
    if (savedFilters.caughtFilter === 'caught' && !isCollected) {
      return false;
    }
    if (savedFilters.caughtFilter === 'uncaught' && isCollected) {
      return false;
    }

    // If no other filters are applied, show creature (already passed caught filter)
    if (!savedFilters.hemisphere && savedFilters.months.size === 0 && !savedFilters.isTimeFilterActive) {
      return true;
    }

    const creatureInfo = getCreatureInfo(creature.name, getCreatureCategory(creature));
    if (!creatureInfo) return true;

    let isMonthAvailable = true;
    let isTimeAvailable = true;

    // Check month availability
    if (savedFilters.months.size > 0) {
      const availableMonths = savedFilters.hemisphere === 'north' 
        ? creatureInfo.northMonths 
        : savedFilters.hemisphere === 'south' 
        ? creatureInfo.southMonths 
        : [...(creatureInfo.northMonths || []), ...(creatureInfo.southMonths || [])];
      
      if (availableMonths) {
        const selectedMonthsArray = Array.from(savedFilters.months);
        isMonthAvailable = selectedMonthsArray.some(month => availableMonths.includes(month));
      }
    }

    // Check time availability
    if (savedFilters.isTimeFilterActive && creatureInfo.availableHours) {
      const { start, end } = savedFilters.timeRange;
      
      if (start <= end) {
        // Normal range (e.g., 6 to 18)
        const timeRange = Array.from({ length: end - start + 1 }, (_, i) => start + i);
        isTimeAvailable = timeRange.some(hour => creatureInfo.availableHours.includes(hour));
      } else {
        // Overnight range (e.g., 18 to 5 = 6PM to 5AM)
        const timeRange = [
          ...Array.from({ length: 24 - start }, (_, i) => start + i), // start to 23
          ...Array.from({ length: end + 1 }, (_, i) => i) // 0 to end
        ];
        isTimeAvailable = timeRange.some(hour => creatureInfo.availableHours.includes(hour));
      }
    }

    return isMonthAvailable && isTimeAvailable;
  };

  // Function to get current data based on selected category and filters
  const getCurrentData = () => {
    let data;
    switch(selectedCategory) {
      case 'bug': data = bugData; break;
      case 'fish': data = fishData; break;
      case 'sea': data = seaData; break;
      case 'all': data = [...bugData, ...fishData, ...seaData]; break;
      default: data = bugData;
    }
    
    // Apply month/hemisphere filtering
    return data.filter(creature => isCreatureAvailable(creature));
  };

  // Function to get category for a specific creature
  const getCreatureCategory = (creature) => {
    if (bugData.includes(creature)) return 'bug';
    if (fishData.includes(creature)) return 'fish';
    if (seaData.includes(creature)) return 'sea';
    return 'unknown';
  };

  // Function to handle creature detail view: navigate to detail route in same tab
  const handleCreatureClick = (creature) => {
    const encoded = encodeURIComponent(creature.name);
    navigate(`/${encoded}`);
  };

  // Note: closing detail is handled via navigation (navigate('/')) inside the detail route

  // Filter modal functions
  const openFilter = () => {
    setIsFilterOpen(true);
  };

  const closeFilter = () => {
    setIsFilterOpen(false);
  };

  const handleHemisphereChange = (hemisphere) => {
    setSelectedHemisphere(hemisphere);
    setIsFilterOpen(false); // Close modal after selection
  };

  const handleApplyFilters = (filters) => {
    setSavedFilters(filters);
    setIsFilterOpen(false);
  };

  // Status modal functions (save / profile modal)
  const openStatus = () => setIsStatusOpen(true);
  const closeStatus = () => setIsStatusOpen(false);

  // Disclaimer / information modal functions
  const openDisclaimer = () => setIsDisclaimerOpen(true);
  const closeDisclaimer = () => setIsDisclaimerOpen(false);

  // Component used for the creature detail route. It reads the creature name from the URL,
  // finds the matching creature from data sources and renders CreatureDetail.
  const CreatureDetailRoute = () => {
    const { creatureName } = useParams();
    const navigate = useNavigate();
    
    // Prevent infinite loops by checking for invalid URLs
    if (!creatureName || creatureName.includes('~') || creatureName.includes('&') || creatureName.includes('?')) {
      navigate('/', { replace: true });
      return null;
    }
    
    const decoded = decodeURIComponent(creatureName);
    const allData = [...bugData, ...fishData, ...seaData];
    const creature = allData.find(c => c.name === decoded);

    if (!creature) {
      return (
        <div style={{ 
          padding: '40px', 
          color: 'white', 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${bg})`,
          backgroundSize: '500px',
          backgroundPosition: 'left top',
          backgroundRepeat: 'repeat'
        }}>
          <h1 style={{ fontSize: '3em', marginBottom: '20px' }}>Whoops!</h1>
          <h2 style={{ fontSize: '1.5em', marginBottom: '30px', fontWeight: 'normal' }}>
            This page doesn't exist
          </h2>
          <p style={{ marginBottom: '40px', fontSize: '1.1em', opacity: 0.8 }}>
            Creature not found: {decoded}
          </p>
          <button 
            onClick={() => navigate('/')} 
            style={{ 
              padding: '15px 40px', 
              fontSize: '1.2em',
              cursor: 'pointer',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontFamily: 'Jua, sans-serif',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#4CAF50'}
          >
            Back to Home
          </button>
        </div>
      );
    }

    const category = getCreatureCategory(creature);

    return (
      <CreatureDetail
        creature={creature}
        category={category}
        onClose={() => navigate('/')}
        isCollected={collectedItems.includes(creature.name)}
        onToggleCollection={() => toggleItemCollection(creature.name)}
      />
    );
  };

  const appStyle = {
    backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${bg})`,
    backgroundSize: '500px',
    backgroundPosition: 'left top',
    backgroundRepeat: 'repeat',
    minHeight: '100vh'
  };

  const HomeUI = (
    <div style={appStyle}>
      <header className="App-header">
        <div className="header-title">
          <img src={blathersImg} alt="Blathers" className="blathers-logo" />
          <h1 className="app-title">Blathers Corner</h1>
        </div>
      </header>

      <div className="App-body">

                <div className="header-controls">
          <Menu 
            selectedCategory={selectedCategory} 
            onCategoryChange={setSelectedCategory} 
          />

          <div className="right-buttons">
            <button className="info-trigger-btn" onClick={openDisclaimer}>
              ℹ️
            </button>
            <button className="info-trigger-btn" onClick={openStatus}>
              💾
            </button>
            <button className="filter-trigger-btn" onClick={openFilter}>
              📅
            </button>
          </div>
        </div>

        <div className="grid">
          {getCurrentData().map((item, i) => {
            const isCollected = collectedItems.includes(item.name);
            return (
              <Gridbutton 
                key={`${selectedCategory}-${i}`}
                topImage={item.image}
                itemName={item.name}
                isCollected={isCollected}
                onTopClick={() => handleCreatureClick(item)}
                onBottomClick={() => toggleItemCollection(item.name)}
              />
            );
          })}
        </div>

      </div>

      {/* Filter Modal */}
      {isFilterOpen && (
        <Filter 
          savedFilters={savedFilters}
          onApplyFilters={handleApplyFilters}
          onClose={closeFilter}
        />
      )}

      {/* Status Modal (save/profile) */}
      {isStatusOpen && (
        <StatusModal 
          onClose={closeStatus}
          collectedItems={collectedItems}
          setCollectedItems={setCollectedItems}
          activeProfile={activeProfile}
          setActiveProfile={setActiveProfile}
          bugData={bugData}
          fishData={fishData}
          seaData={seaData}
        />
      )}

      {/* Disclaimer / Info Modal */}
      {isDisclaimerOpen && (
        <DisclaimerModal onClose={closeDisclaimer} />
      )}
    </div>
  );

  return (
    <Routes>
      <Route path="/" element={HomeUI} />
      <Route path="/:creatureName" element={<CreatureDetailRoute />} />
    </Routes>
  );
}

export default App;